import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";

const SDK_URL = {
  sandbox: "https://sandbox.web.squarecdn.com/v1/square.js",
  production: "https://web.squarecdn.com/v1/square.js",
};

function loadSquareSdk(src) {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve();
    const existing = document.querySelector(`script[data-square="1"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Square SDK failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.dataset.square = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Square SDK failed to load"));
    document.head.appendChild(s);
  });
}

export function SquarePayment({ jobId, amountLabel, total, collected, onPaid }) {
  const [cfg, setCfg] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [depositAmt, setDepositAmt] = useState("");
  const cardRef = useRef(null);
  const domId = `sq-card-${jobId}`;
  const remaining = Math.max(0, Number(total || 0) - Number(collected || 0));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/square/config");
        if (cancelled) return;
        setCfg(data);
        if (!data.enabled) return;
        await loadSquareSdk(SDK_URL[data.environment] || SDK_URL.sandbox);
        if (cancelled || !window.Square) return;
        const payments = window.Square.payments(data.application_id, data.location_id);
        const card = await payments.card();
        await card.attach(`#${domId}`);
        if (cancelled) { try { card.destroy(); } catch { /* noop */ } return; }
        cardRef.current = card;
        setReady(true);
      } catch (e) {
        console.error("Square init failed", e);
      }
    })();
    return () => { cancelled = true; try { cardRef.current?.destroy(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pay = async (kind = "full") => {
    if (!cardRef.current || busy) return;
    if (kind === "deposit" && (!depositAmt || Number(depositAmt) <= 0 || Number(depositAmt) > remaining)) {
      toast.error(`Enter a deposit between $1 and $${remaining.toLocaleString()}`); return;
    }
    setBusy(true);
    try {
      const res = await cardRef.current.tokenize();
      if (res.status !== "OK") throw new Error(res.errors?.[0]?.message || "Card was not accepted");
      const attempt = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `att-${Date.now()}`;
      const payload = { source_id: res.token, payment_attempt_id: attempt, kind };
      if (kind === "deposit") payload.amount = Number(depositAmt);
      const { data } = await api.post(`/jobs/${jobId}/square/pay`, payload);
      if (data.receipt_url) window.open(data.receipt_url, "_blank", "noopener");
      if (!data.service_locked) toast.success("Payment confirmed by Square — Job Mode unlocked");
      else toast.message(`Payment ${data.status || "recorded"} — ${data.payment_state}`);
      onPaid?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  if (cfg && !cfg.enabled) return null; // Square not configured → manual flow shows instead
  return (
    <div className="sg-card-elev p-4 mb-3" data-testid="square-payment">
      <div className="font-head font-bold mb-2 flex items-center gap-2">
        <CreditCard size={16} style={{ color: "var(--accent)" }} /> Pay by Card
        {cfg?.environment === "sandbox" && <span className="chip" style={{ fontSize: 10 }}>SANDBOX</span>}
      </div>
      <div id={domId} data-testid="square-card-container" style={{ minHeight: 46 }} />
      {Number(collected || 0) > 0 && (
        <div className="text-xs mt-2" style={{ color: "var(--txt2)" }} data-testid="square-balance">
          Job total ${Number(total || 0).toLocaleString()} · Collected ${Number(collected || 0).toLocaleString()} · Remaining <b style={{ color: "var(--txt)" }}>${remaining.toLocaleString()}</b>
        </div>
      )}
      <button className="sg-btn sg-btn-accent w-full mt-3" style={{ minHeight: 44 }}
        disabled={!ready || busy} onClick={() => pay("full")} data-testid="square-pay-btn">
        {busy ? "Processing…" : `Charge Card ${amountLabel || ""}`}
      </button>
      <div className="flex gap-2 mt-2 items-center">
        <input type="number" min="1" step="1" value={depositAmt} onChange={(e) => setDepositAmt(e.target.value)}
          placeholder="Deposit $" data-testid="square-deposit-input"
          className="sg-input" style={{ maxWidth: 120, padding: "8px 10px" }} />
        <button className="sg-btn flex-1" style={{ minHeight: 40 }} disabled={!ready || busy}
          onClick={() => pay("deposit")} data-testid="square-deposit-btn">Charge Deposit</button>
      </div>
      <div className="text-[11px] mt-2" style={{ color: "var(--muted)" }}>
        Card details go directly to Square. A deposit does NOT mark the job paid — the job only unlocks when the full balance clears.
      </div>
    </div>
  );
}
